import { Skeleton } from '../Skeleton';
import { useCallback, useEffect, useState } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

export interface CodeFile {
  id: string;
  path: string;
  kind: string;
  content: string | null;
  isEntry: boolean;
}

/**
 * Looking at the code your prompts produced (spec 5b.8). Free users can read it once they turn on
 * experimental mode — seeing the real output is a big part of the learning; only editing it by hand
 * is Pro. Both rules are enforced by the API, not just hidden here.
 */
export function CodePanel({
  projectId,
  selectedId,
  onOpenFile,
}: {
  projectId: string;
  selectedId: string | null;
  /** the explorer only CHOOSES; the workspace editor does the editing */
  onOpenFile: (file: CodeFile, editable: boolean) => void;
}) {
  const { experimental, setExperimental } = useAuth();
  const [files, setFiles] = useState<CodeFile[] | null>(null);
  const [editable, setEditable] = useState(false);
  const [blocked, setBlocked] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ files: CodeFile[]; editable: boolean }>(
        `/box/projects/${projectId}/code`,
      );
      setFiles(data.files);
      setEditable(data.editable);
      setBlocked(null);
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'experimental_off') {
        setBlocked('experimental');
        setFiles(null);
        return;
      }
      setBlocked('error');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const enableExperimental = async () => {
    try {
      await api.patch('/box/me/flags', { experimental: true });
      setExperimental(true);
      await load();
    } catch {
      setBlocked('error');
    }
  };

  if (blocked === 'experimental' || (!experimental && !files)) {
    return (
      <div className="space-y-3 p-4">
        <h3 className="text-sm font-medium">See the code behind your app</h3>
        <p className="text-sm text-muted">
          Every prompt you write produces real code. Turning this on shows it — you do not need to
          understand it to keep building.
        </p>
        <button
          type="button"
          onClick={() => void enableExperimental()}
          className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-accent-ink transition-opacity hover:opacity-90"
        >
          Show me the code
        </button>
      </div>
    );
  }

  if (blocked === 'error') return <p className="p-3 text-sm text-muted">Could not load the code.</p>;
  if (!files)
    return (
      <div className="space-y-3 p-3" aria-busy="true" aria-label="Loading files">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="flex items-center gap-2">
            <Skeleton className="h-4 w-4 shrink-0" />
            <Skeleton className={i % 2 ? "h-3 w-1/2" : "h-3 w-2/3"} />
          </div>
        ))}
      </div>
    );
  if (files.length === 0) {
    return <p className="p-3 text-sm text-muted">Nothing built yet — no code to show.</p>;
  }

  /*
   * The rail is an EXPLORER now — a horizontal strip of tab buttons hid most paths off-screen and
   * squeezed the editor into a 380px column. Files group by folder, sources first (they are what a
   * learner edits; dist/ is compiler output shown last and clearly secondary).
   */
  const groups = new Map<string, CodeFile[]>();
  for (const file of files) {
    const dir = file.path.includes('/') ? file.path.slice(0, file.path.lastIndexOf('/')) : '';
    if (!groups.has(dir)) groups.set(dir, []);
    groups.get(dir)!.push(file);
  }
  const ordered = [...groups.entries()].sort(([a], [b]) => {
    const rank = (d: string) => (d.startsWith('src') ? 0 : d === '' ? 1 : 2);
    return rank(a) - rank(b) || a.localeCompare(b);
  });

  return (
    <div className="flex h-full min-h-0 flex-col overflow-auto px-2 pb-3">
      {ordered.map(([dir, dirFiles]) => (
        <div key={dir || '(root)'} className="mb-1">
          <p className="px-2 pb-1 pt-2 font-mono text-[10px] font-bold uppercase tracking-wider text-muted">
            {dir || 'project'}
          </p>
          {dirFiles.map(file => {
            const name = file.path.slice(file.path.lastIndexOf('/') + 1);
            const selected = file.id === selectedId;
            return (
              <button
                key={file.id}
                type="button"
                onClick={() => onOpenFile(file, editable)}
                className={[
                  'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left font-mono text-xs transition',
                  selected ? 'bg-accent/10 font-semibold text-accent-deep' : 'text-ink hover:bg-page',
                ].join(' ')}
              >
                <FileIcon path={file.path} />
                <span className="min-w-0 flex-1 truncate">{name}</span>
              </button>
            );
          })}
        </div>
      ))}
      <p className="mt-auto px-2 pt-3 text-[11px] text-muted">
        {editable ? 'Pick a file to edit it in the workspace.' : 'Pick a file to read it — editing is a Pro feature.'}
      </p>
    </div>
  );
}

/** Tiny type cue per file — enough to scan the tree without reading extensions. */
function FileIcon({ path }: { path: string }) {
  const color = /\.(jsx|tsx)$/.test(path)
    ? 'text-accent-deep'
    : /\.css$/.test(path)
      ? 'text-blue-500'
      : /\.(js|mjs|ts)$/.test(path)
        ? 'text-amber-500'
        : 'text-muted';
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 ${color}`} aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}
